/**
 * On-device LLM via transformers.js (WebGPU, INT4 quant).
 *
 * Model defaults:
 *   - text generation: Xenova/Phi-3.5-mini-instruct-onnx-web (q4 ~2GB)
 *   - embeddings:      Xenova/all-MiniLM-L6-v2 (~25MB)
 *
 * Lazy load — first call kicks downloads + WebGPU init. Subsequent calls hit cache.
 * Always wrap in try/catch and fall back to remote OpenRouter Ring-2.6-1T free.
 */

const TJS_URL = 'https://esm.sh/@huggingface/transformers@3.0.2?bundle';

interface TJSMod {
  pipeline: <T>(task: string, model?: string, opts?: Record<string, unknown>) => Promise<T>;
  env: { allowLocalModels: boolean; allowRemoteModels: boolean; backends: Record<string, unknown> };
}

let modPromise: Promise<TJSMod> | null = null;

export function loadTransformers(): Promise<TJSMod> {
  if (modPromise === null) {
    modPromise = import(/* @vite-ignore */ TJS_URL).then((m) => m as TJSMod);
  }
  return modPromise;
}

type EmbedPipeline = (text: string, opts?: { pooling?: string; normalize?: boolean }) => Promise<{ data: Float32Array }>;
type GenPipeline = (
  text: string | Array<{ role: string; content: string }>,
  opts?: { max_new_tokens?: number; temperature?: number; do_sample?: boolean },
) => Promise<Array<{ generated_text?: string | Array<{ role: string; content: string }> }>>;

let embedPromise: Promise<EmbedPipeline> | null = null;
let genPromise: Promise<GenPipeline> | null = null;

export async function getEmbedder(): Promise<EmbedPipeline> {
  if (embedPromise === null) {
    embedPromise = loadTransformers().then((m) => m.pipeline<EmbedPipeline>('feature-extraction', 'Xenova/all-MiniLM-L6-v2'));
  }
  return embedPromise;
}

export async function embed(text: string): Promise<Float32Array> {
  const pipe = await getEmbedder();
  const out = await pipe(text, { pooling: 'mean', normalize: true });
  return out.data;
}

export async function embedBatch(texts: string[]): Promise<Float32Array[]> {
  const pipe = await getEmbedder();
  const all: Float32Array[] = [];
  for (const t of texts) {
    const out = await pipe(t, { pooling: 'mean', normalize: true });
    all.push(out.data);
  }
  return all;
}

export interface GenOptions {
  readonly maxTokens?: number;
  readonly temperature?: number;
}

export async function generate(prompt: string, opts: GenOptions = {}): Promise<string> {
  try {
    if (genPromise === null) {
      genPromise = loadTransformers().then((m) =>
        m.pipeline<GenPipeline>('text-generation', 'Xenova/Phi-3.5-mini-instruct-onnx-web', { device: 'webgpu', dtype: 'q4' }),
      );
    }
    const gen = await genPromise;
    const out = await gen(prompt, {
      max_new_tokens: opts.maxTokens ?? 256,
      temperature: opts.temperature ?? 0.3,
      do_sample: (opts.temperature ?? 0.3) > 0,
    });
    const first = out[0];
    if (first === undefined || first.generated_text === undefined) return '';
    return typeof first.generated_text === 'string' ? first.generated_text : '';
  } catch {
    return remoteFallback(prompt, opts);
  }
}

async function remoteFallback(prompt: string, opts: GenOptions): Promise<string> {
  try {
    const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        model: 'inclusionai/ring-flash-2.0:free',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: opts.maxTokens ?? 256,
        temperature: opts.temperature ?? 0.3,
      }),
    });
    if (!res.ok) return '';
    const body = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
    return body.choices?.[0]?.message?.content ?? '';
  } catch {
    return '';
  }
}

export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    dot += ai * bi;
    na += ai * ai;
    nb += bi * bi;
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom === 0 ? 0 : dot / denom;
}
